/**
 * Studio catalog Export + Import buttons — Issue #154.
 *
 * Drop-in toolbar component for any Studio catalog list page (Tools,
 * Skills, Prompts, Agents, etc.). Renders two buttons:
 *
 *   - Export: GETs `${baseUrl}/export` with optional ?ids filter, gets
 *     back an argus-bundle.v1 JSON, triggers a browser download.
 *
 *   - Import: opens a hidden <input type="file" />, parses the picked
 *     bundle, POSTs to `${baseUrl}/import?onConflict=skip`, and shows
 *     a toast summary with the {created, updated, skipped, errored}
 *     counts. Calls `onImported()` so the parent page can refetch.
 *
 * Permissions: the backend gates POST /import with the catalog's
 * write permission; this component does NOT pre-check — it surfaces a
 * 403 from the API as a toast. Hiding the button by role is the
 * parent's job (e.g. `useAuthRole()`).
 *
 * Visual: matches the existing "Register Tool" + "Add" button style
 * used elsewhere in Studio.
 */

import { useRef, useState } from 'react';
import { Download, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

export interface CatalogExportImportButtonsProps {
  /**
   * Base API path for the catalog. Examples:
   *   /api/v1/workspace/tools
   *   /api/v1/studio-skills
   *   /api/v1/templates
   *
   * `${baseUrl}/export` and `${baseUrl}/import` must exist on the
   * server. The parent passes the same baseUrl its list query uses.
   */
  baseUrl: string;

  /** Human-friendly kind label used in toasts ("tools", "skills", etc.). */
  kindLabel: string;

  /** Conflict resolution mode. Default 'skip'. */
  onConflict?: 'skip' | 'overwrite';

  /** Called after a successful import so the parent can refetch its list. */
  onImported?: () => void;

  /** Disable both buttons (e.g. while the parent's list is loading). */
  disabled?: boolean;
}

interface ImportResult {
  bundleKind: string;
  bundleId: string;
  counts: { created: number; updated: number; skipped: number; errored: number };
  errors?: Array<{ key: string | null; message: string }>;
}

export function CatalogExportImportButtons({
  baseUrl,
  kindLabel,
  onConflict = 'skip',
  onImported,
  disabled = false,
}: CatalogExportImportButtonsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${baseUrl}/export`, {
        method: 'GET',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const body = await res.text();
        let msg = `Export failed (${res.status})`;
        try {
          const json = JSON.parse(body);
          if (json?.error?.message) msg = json.error.message;
        } catch {
          // body wasn't JSON; keep generic message
        }
        toast.error(`Export failed`, { description: msg });
        return;
      }
      // Browser download via blob → <a download>.
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') ?? '';
      const match = /filename="?([^";]+)"?/.exec(cd);
      const filename = match?.[1] ?? `${kindLabel}-export.argus-bundle.json`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${kindLabel}`, {
        description: filename,
      });
    } catch (err) {
      toast.error('Export failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setExporting(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFilePicked = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset the input so the user can pick the same file twice in a row.
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch (parseErr) {
        toast.error('Import failed', {
          description: 'File is not valid JSON.',
        });
        return;
      }
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${baseUrl}/import?onConflict=${onConflict}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(parsed),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = body?.error?.message ?? `Import failed (${res.status})`;
        toast.error('Import failed', { description: msg });
        return;
      }
      const result = body as ImportResult;
      const { created, updated, skipped, errored } = result.counts;
      toast.success(`Imported ${kindLabel}`, {
        description:
          `${created} created, ${updated} updated, ${skipped} skipped` +
          (errored > 0 ? `, ${errored} errored` : ''),
      });
      if (errored > 0 && result.errors && result.errors.length > 0) {
        // Log details to console for the user to inspect — toast can't fit them.
        // eslint-disable-next-line no-console
        console.warn('Import errors:', result.errors);
      }
      onImported?.();
    } catch (err) {
      toast.error('Import failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        disabled={disabled || exporting}
        title={`Download all ${kindLabel} as an argus-bundle file`}
      >
        {exporting ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Download className="h-3.5 w-3.5" />
        )}
        Export
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={handleImportClick}
        disabled={disabled || importing}
        title={`Import a ${kindLabel} bundle (existing rows are ${onConflict === 'skip' ? 'skipped' : 'overwritten'})`}
      >
        {importing ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Upload className="h-3.5 w-3.5" />
        )}
        Import
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,.argus-bundle,application/json"
        className="hidden"
        onChange={handleFilePicked}
      />
    </>
  );
}
